import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  DialogActions,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import dayjs from "dayjs";
import {
  defaultPromotionEventTime,
  hasEventTimeValue,
  resolvePromotionTimePickerValue,
} from "./promotionEventDateTime";
import "./PromotionTimePicker.css";

const TimePickerActionBar = ({ onAccept, onCancel }) => (
  <DialogActions className="promo-time-picker__actions">
    <Button
      type="button"
      className="promo-time-picker__action"
      onClick={onCancel}
    >
      Cancel
    </Button>
    <Button
      type="button"
      className="promo-time-picker__action promo-time-picker__action--ok"
      onClick={onAccept}
    >
      OK
    </Button>
  </DialogActions>
);

const currentTime = (value) =>
  value && dayjs(value).isValid()
    ? dayjs(value)
    : dayjs().second(0).millisecond(0);

const digitsOnly = (value, max) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, max);

const constrainHourDraft = (raw) => {
  if (raw === "") return "";
  const n = Number(raw);
  if (Number.isNaN(n)) return "";
  if (raw.length === 1) return raw;
  return String(Math.min(12, n === 0 ? 12 : n));
};

const constrainMinuteDraft = (raw) => {
  if (raw === "") return "";
  const n = Number(raw);
  if (Number.isNaN(n)) return "";
  if (raw.length === 1) return raw;
  return String(Math.min(59, Math.max(0, n))).padStart(2, "0");
};

const normalizeHour = (value) => {
  const n = Number(value);
  if (!value || Number.isNaN(n)) return null;
  if (n === 0) return 12;
  return Math.min(12, Math.max(1, n));
};

const normalizeMinute = (value) => {
  const n = Number(value);
  if (value === "" || Number.isNaN(n)) return null;
  return Math.min(59, Math.max(0, n));
};

const PromotionTimeToolbar = ({
  parsedValue,
  openView,
  setOpenView,
  onChange,
  ampm = true,
  toolbarTitle = "Select time",
  disabled = false,
  readOnly = false,
}) => {
  const time =
    parsedValue && dayjs(parsedValue).isValid() ? dayjs(parsedValue) : null;
  const isPm = time ? time.hour() >= 12 : false;
  const canEdit = !disabled && !readOnly;

  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const hourFocused = useRef(false);
  const minuteFocused = useRef(false);
  const restoreFocus = useRef(null);

  const [hourDraft, setHourDraft] = useState(null);
  const [minuteDraft, setMinuteDraft] = useState(null);

  useEffect(() => {
    if (!hourFocused.current) setHourDraft(null);
    if (!minuteFocused.current) setMinuteDraft(null);
  }, [parsedValue]);

  useEffect(() => {
    const target = restoreFocus.current;
    if (!target) return;
    const node = target === "hours" ? hourRef.current : minuteRef.current;
    restoreFocus.current = null;
    requestAnimationFrame(() => {
      node?.focus();
      node?.select();
    });
  }, [openView]);

  const applyHour = (hour12) => {
    const base = currentTime(parsedValue);
    const pm = base.hour() >= 12;
    const hour24 = (hour12 % 12) + (pm ? 12 : 0);
    onChange(base.hour(hour24).second(0).millisecond(0), "partial");
  };

  const applyMinute = (minute) => {
    onChange(
      currentTime(parsedValue).minute(minute).second(0).millisecond(0),
      "partial"
    );
  };

  const commitHour = (raw) => {
    const hour12 = normalizeHour(raw);
    setHourDraft(null);
    if (hour12 != null) applyHour(hour12);
  };

  const commitMinute = (raw) => {
    const minute = normalizeMinute(raw);
    setMinuteDraft(null);
    if (minute != null) applyMinute(minute);
  };

  const focusView = (view) => {
    if (openView === view) return;
    restoreFocus.current = view;
    setOpenView(view);
  };

  const handleHourChange = (event) => {
    const raw = constrainHourDraft(digitsOnly(event.target.value, 2));
    setHourDraft(raw);
    if (raw === "") return;
    const n = Number(raw);
    if (n >= 2 && n <= 9) {
      applyHour(n);
      restoreFocus.current = "minutes";
      setOpenView("minutes");
      return;
    }
    if (raw.length === 2) {
      const hour12 = normalizeHour(raw);
      if (hour12 != null) applyHour(hour12);
      restoreFocus.current = "minutes";
      setOpenView("minutes");
    } else if (n === 1) {
      applyHour(1);
    }
  };

  const handleMinuteChange = (event) => {
    const raw = constrainMinuteDraft(digitsOnly(event.target.value, 2));
    setMinuteDraft(raw);
    if (raw === "") return;
    const n = Number(raw);
    if (n >= 6 && raw.length === 1) {
      applyMinute(n);
      return;
    }
    if (raw.length === 2) {
      const minute = normalizeMinute(raw);
      if (minute != null) applyMinute(minute);
    }
  };

  const setMeridiem = (nextPm) => {
    if (!canEdit) return;
    const base = currentTime(parsedValue);
    const hour12 = base.hour() % 12;
    onChange(base.hour(nextPm ? hour12 + 12 : hour12), "partial");
  };

  const hourValue =
    hourDraft ?? (time ? time.format("h") : "");
  const minuteValue =
    minuteDraft ?? (time ? time.format("mm") : "");

  return (
    <div className="promo-time-picker__toolbar">
      <p className="promo-time-picker__toolbar-title">{toolbarTitle}</p>
      <div className="promo-time-picker__toolbar-row">
        <input
          ref={hourRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={2}
          aria-label="Hours"
          disabled={!canEdit}
          value={hourValue}
          placeholder="--"
          className={`promo-time-picker__digit${
            openView === "hours" ? " promo-time-picker__digit--active" : ""
          }`}
          onChange={handleHourChange}
          onFocus={(event) => {
            hourFocused.current = true;
            event.target.select();
            focusView("hours");
          }}
          onBlur={(event) => {
            hourFocused.current = false;
            commitHour(event.target.value);
          }}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === "Enter") event.target.blur();
          }}
        />
        <span className="promo-time-picker__colon">:</span>
        <input
          ref={minuteRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={2}
          aria-label="Minutes"
          disabled={!canEdit}
          value={minuteValue}
          placeholder="--"
          className={`promo-time-picker__digit${
            openView === "minutes" ? " promo-time-picker__digit--active" : ""
          }`}
          onChange={handleMinuteChange}
          onFocus={(event) => {
            minuteFocused.current = true;
            event.target.select();
            focusView("minutes");
          }}
          onBlur={(event) => {
            minuteFocused.current = false;
            commitMinute(event.target.value);
          }}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === "Enter") event.target.blur();
          }}
        />
        {ampm && (
          <div className="promo-time-picker__ampm" role="group" aria-label="AM or PM">
            <button
              type="button"
              className={`promo-time-picker__ampm-btn${
                !isPm ? " promo-time-picker__ampm-btn--active" : ""
              }`}
              onClick={() => setMeridiem(false)}
              disabled={!canEdit}
            >
              AM
            </button>
            <button
              type="button"
              className={`promo-time-picker__ampm-btn${
                isPm ? " promo-time-picker__ampm-btn--active" : ""
              }`}
              onClick={() => setMeridiem(true)}
              disabled={!canEdit}
            >
              PM
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const PromotionTimePicker = ({
  value,
  onChange,
  onBlur,
  error = false,
  helperText,
  textFieldSx,
  disabled = false,
  applyDefaultOnOpen = false,
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const draftRef = useRef(value);
  draftRef.current = draft;

  useEffect(() => {
    if (!open) setDraft(value);
  }, [open, value]);

  const pickerValue = applyDefaultOnOpen ? (open ? draft : value) : value;

  const handleOpen = () => {
    if (applyDefaultOnOpen) {
      setDraft(
        resolvePromotionTimePickerValue({
          value,
          open: true,
          applyDefaultOnOpen: true,
        })
      );
    }
    setOpen(true);
  };

  const commitDraft = () => {
    const next = hasEventTimeValue(draftRef.current)
      ? draftRef.current
      : defaultPromotionEventTime();
    onChange(next);
  };

  return (
  <MobileTimePicker
    ampm
    showToolbar
    disableMaskedInput
    value={pickerValue}
    onOpen={handleOpen}
    onChange={(next) => {
      if (applyDefaultOnOpen) {
        setDraft(next);
        return;
      }
      onChange(next);
    }}
    onClose={() => {
      setOpen(false);
      onBlur?.();
    }}
    disabled={disabled}
    toolbarTitle="Select time"
    inputFormat="hh:mm A"
    minutesStep={1}
    views={["hours", "minutes"]}
    openTo="hours"
    ToolbarComponent={PromotionTimeToolbar}
    components={{
      OpenPickerIcon: AccessTimeOutlinedIcon,
      ActionBar: (actionProps) => (
        <TimePickerActionBar
          onCancel={actionProps.onCancel}
          onAccept={() => {
            if (applyDefaultOnOpen) commitDraft();
            actionProps.onAccept();
          }}
        />
      ),
    }}
    DialogProps={{
      className: "promo-time-picker-dialog",
      sx: { zIndex: 2000 },
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        fullWidth
        placeholder="Select Time"
        onBlur={onBlur}
        error={error}
        helperText={helperText}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                edge="end"
                disabled={disabled}
                tabIndex={-1}
                aria-hidden
                sx={{ pointerEvents: "none" }}
              >
                <AccessTimeOutlinedIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          ...textFieldSx,
          "& .MuiInputBase-root": { cursor: "pointer" },
          "& .MuiInputBase-input": {
            ...(textFieldSx?.["& .MuiInputBase-input"] || {}),
            cursor: "pointer",
          },
        }}
      />
    )}
  />
  );
};
